import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, usePoll } from "../lib/api";
import { useData } from "../lib/DataContext";
import { Panel, Qty } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const inputCls = "h-12 w-full px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]";

function BundleDialog({ form, setForm, onSaved }) {
  const { items } = useData();
  const t3 = items.filter((i) => i.tier === 3);
  const setRow = (i, p) => setForm({ ...form, components: form.components.map((c, j) => (j === i ? { ...c, ...p } : c)) });
  const save = async () => {
    const body = { ...form, components: form.components.filter((c) => c.item_id).map((c) => ({ item_id: c.item_id, qty: Number(c.qty) })) };
    try {
      if (form.id) await api.put(`/bundles/${form.id}`, body); else await api.post("/bundles", body);
      toast.success("Bundle saved"); setForm(null); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-2xl" data-testid="bundle-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} Kit / Bundle</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">SHOPIFY PARENT SKU</span>
            <input data-testid="bundle-sku-input" className={`${inputCls} font-mono`} value={form.parent_sku} onChange={(e) => setForm({ ...form, parent_sku: e.target.value })} placeholder="FG-PADDED-SLING" /></label>
          <label className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">NAME</span>
            <input data-testid="bundle-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Padded Sling" /></label>
        </div>
        <div className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">TIER 3 COMPONENTS CONSUMED PER KIT</div>
        {form.components.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`bundle-row-${i}`}>
            <select data-testid={`bundle-component-select-${i}`} value={c.item_id} onChange={(e) => setRow(i, { item_id: e.target.value })} className={`${inputCls} col-span-8 bg-[#090D16]`}>
              <option value="">Select Tier 3 item…</option>
              {t3.map((it) => <option key={it.id} value={it.id}>{it.name} · {it.sku}</option>)}
            </select>
            <input data-testid={`bundle-qty-input-${i}`} type="number" step="any" value={c.qty} onChange={(e) => setRow(i, { qty: e.target.value })} className={`${inputCls} col-span-3 font-mono text-right`} />
            <button data-testid={`bundle-remove-row-${i}`} onClick={() => setForm({ ...form, components: form.components.filter((_, j) => j !== i) })} className="col-span-1 h-12 flex items-center justify-center text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button data-testid="bundle-add-row-btn" onClick={() => setForm({ ...form, components: [...form.components, { item_id: "", qty: 1 }] })} className="h-12 rounded-md border border-dashed border-[#334155] text-[#9CA3AF] flex items-center justify-center gap-2"><Plus className="w-4 h-4" />Add component</button>
        <button data-testid="bundle-save-btn" disabled={!form.parent_sku || !form.name} onClick={save} className="h-14 rounded-md bg-[#F59E0B] text-[#090D16] font-display font-extrabold disabled:opacity-40">SAVE BUNDLE</button>
      </DialogContent>
    </Dialog>
  );
}

export default function BundlesPanel() {
  const { itemsById } = useData();
  const { data, refresh } = usePoll("/bundles", 10000);
  const [form, setForm] = useState(null);
  const del = async (b) => {
    if (!window.confirm(`Delete bundle ${b.name}?`)) return;
    try { await api.delete(`/bundles/${b.id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[#9CA3AF] max-w-2xl">A bundle maps one Shopify SKU to several Tier 3 items. A paid Shopify order for the bundle deducts every component at once, or nothing if any component is missing or short. Bundle stock is read-only: it is the lowest number of kits any component's Item Master stock allows.</p>
        <button data-testid="bundle-new-btn" onClick={() => setForm({ parent_sku: "", name: "", components: [{ item_id: "", qty: 1 }] })} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><Plus className="w-4 h-4" />New Bundle</button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data || []).map((b) => (
          <Panel key={b.id} className="p-5" data-testid={`bundle-card-${b.parent_sku}`}>
            <div className="flex justify-between gap-2">
              <div><div className="font-display text-lg font-bold">{b.name}</div><div className="font-mono text-xs text-[#38BDF8]">{b.parent_sku}</div></div>
              <div className="flex gap-1">
                <button data-testid={`bundle-edit-${b.parent_sku}`} onClick={() => setForm(b)} className="h-10 w-10 flex items-center justify-center rounded hover:bg-[#172033]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`bundle-delete-${b.parent_sku}`} onClick={() => del(b)} className="h-10 w-10 flex items-center justify-center rounded text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {b.components.map((c) => {
                const it = itemsById[c.item_id];
                const lim = b.limiting_item_id === c.item_id;
                return (
                  <div key={c.item_id} data-testid={`bundle-component-${b.parent_sku}-${it?.sku || "missing"}`} className="flex justify-between gap-2 text-sm">
                    <span className={`truncate ${it ? "" : "text-[#EF4444]"}`}>{it?.name || "Missing item"}{lim && <span className="ml-2 font-mono text-[9px] font-bold text-[#F59E0B]" data-tip="This component sets the bundle stock">LIMITING</span>}</span>
                    <span className="font-mono text-xs text-[#9CA3AF] whitespace-nowrap"><Qty v={c.qty} unit="×" className="text-[#F59E0B]" /> · {it ? `${Math.floor(Math.max(0, it.stock))} in stock` : "—"}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t border-[#1E293B] flex items-center justify-between" data-tip="Computed from the lowest-stock component. Read-only.">
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">BUNDLE STOCK · READ ONLY</span>
              <span data-testid={`bundle-kits-${b.parent_sku}`} className="font-display text-2xl font-extrabold text-white">{b.available_stock ?? 0}</span>
            </div>
          </Panel>
        ))}
        {data && !data.length && <div className="text-[#6B7280] font-mono" data-testid="bundles-empty">NO BUNDLES YET</div>}
      </div>
      {form && <BundleDialog form={form} setForm={setForm} onSaved={refresh} />}
    </div>
  );
}
